import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, Plus, Trash2, Save, ChevronDown, ChevronUp, Pencil, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POSITION_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'linha', label: 'Só Linha' },
  { value: 'defesa+gr', label: 'Defesa + GR' },
  { value: 'gr', label: 'Só Guarda-Redes' },
];

const requirementToValue = (req) => {
  if (!req || req.length === 0) return 'all';
  const set = new Set(req);
  if (set.has('linha') && !set.has('defesa') && !set.has('guarda_redes')) return 'linha';
  if (set.has('defesa') && set.has('guarda_redes') && !set.has('linha')) return 'defesa+gr';
  if (set.has('guarda_redes') && !set.has('linha') && !set.has('defesa')) return 'gr';
  return 'all';
};

const valueToRequirement = (v) => {
  switch (v) {
    case 'linha': return ['linha'];
    case 'defesa+gr': return ['defesa', 'guarda_redes'];
    case 'gr': return ['guarda_redes'];
    default: return null;
  }
};

const RatingConfigSection = () => {
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingWeights, setEditingWeights] = useState({});
  const [editingLabels, setEditingLabels] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [newField, setNewField] = useState({ label: '', key: '', weight: 1, position: 'all' });
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/rating-config`);
      setConfig(res.data?.config || []);
    } catch (e) {
      console.error('Erro a carregar config', e);
    }
    setLoading(false);
  };

  const handleSaveWeight = async (key) => {
    const newW = editingWeights[key];
    if (newW === undefined) return;
    try {
      await axios.put(`${API}/admin/rating-config`, { key, weight: parseFloat(newW) });
      setEditingWeights(prev => { const c = { ...prev }; delete c[key]; return c; });
      fetchConfig();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a guardar peso');
    }
  };

  const handleAddField = async () => {
    if (!newField.label.trim() || !newField.key.trim()) {
      alert('Preenche o nome e a chave do campo');
      return;
    }
    try {
      await axios.post(`${API}/admin/rating-config/custom-field`, {
        key: newField.key.trim(),
        label: newField.label.trim(),
        weight: parseFloat(newField.weight) || 0,
        position_required: valueToRequirement(newField.position),
      });
      setNewField({ label: '', key: '', weight: 1, position: 'all' });
      setShowAdd(false);
      fetchConfig();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a adicionar campo');
    }
  };

  const handleDeleteCustom = async (key) => {
    if (!window.confirm(`Remover o campo "${key}"? Esta acção apaga este campo de todos os jogadores.`)) return;
    try {
      await axios.delete(`${API}/admin/rating-config/custom-field/${key}`);
      fetchConfig();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a remover');
    }
  };

  const handleToggle = async (key, enabled) => {
    try {
      await axios.put(`${API}/admin/rating-config/toggle`, { key, enabled });
      fetchConfig();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a alterar visibilidade');
    }
  };

  const handleSaveLabel = async (key) => {
    const newLabel = (editingLabels[key] || '').trim();
    if (!newLabel) { alert('O nome não pode ficar vazio'); return; }
    try {
      await axios.put(`${API}/admin/rating-config/label`, { key, label: newLabel });
      setEditingLabels(prev => { const c = { ...prev }; delete c[key]; return c; });
      fetchConfig();
    } catch (e) {
      alert(e.response?.data?.detail || 'Erro a renomear');
    }
  };

  if (loading) return <div className="p-4 text-zinc-500">A carregar configuração...</div>;

  return (
    <div className="mb-6 border-2 border-indigo-500 bg-indigo-50 rounded-lg overflow-hidden" data-testid="rating-config-section">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between p-4 hover:bg-indigo-100 transition-colors"
        data-testid="toggle-rating-config"
      >
        <div className="flex items-center gap-2">
          <Settings size={22} className="text-indigo-700" />
          <h3 className="text-xl font-bold text-indigo-800">⚙️ CONFIGURAÇÃO DE PONTOS</h3>
          <span className="text-xs text-indigo-600 bg-white px-2 py-0.5 rounded-full font-bold">{config.length}</span>
        </div>
        {collapsed ? <ChevronDown size={22} className="text-indigo-700" /> : <ChevronUp size={22} className="text-indigo-700" />}
      </button>

      {!collapsed && (
        <div className="p-6 pt-2 border-t-2 border-indigo-200">
          <p className="text-sm text-gray-700 mb-4">
            Aqui defines o peso de cada acção na fórmula de Pontos_Rating. Mudanças aqui recalculam todo o histórico automaticamente.
            <br/><span className="text-xs text-gray-500">Dica: define peso = 0 para desactivar um campo do sistema sem o remover.</span>
          </p>

      <div className="space-y-2 mb-4">
        {config.map((item) => {
          const isEditing = editingWeights[item.key] !== undefined;
          const displayWeight = isEditing ? editingWeights[item.key] : item.weight;
          const enabled = item.enabled !== false;
          return (
            <div
              key={item.key}
              className={`flex items-center gap-3 p-3 bg-white border-2 rounded ${enabled ? 'border-zinc-200' : 'border-zinc-300 opacity-60'}`}
              data-testid={`config-row-${item.key}`}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {editingLabels[item.key] !== undefined ? (
                    <>
                      <input
                        type="text"
                        value={editingLabels[item.key]}
                        onChange={(e) => setEditingLabels(prev => ({ ...prev, [item.key]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveLabel(item.key); if (e.key === 'Escape') setEditingLabels(prev => { const c = { ...prev }; delete c[item.key]; return c; }); }}
                        className="font-bold px-2 py-0.5 border-2 border-indigo-400 rounded"
                        autoFocus
                        data-testid={`label-input-${item.key}`}
                      />
                      <button
                        onClick={() => handleSaveLabel(item.key)}
                        className="p-1 bg-green-500 hover:bg-green-600 text-white rounded"
                        title="Guardar novo nome"
                        data-testid={`save-label-${item.key}`}
                      >
                        <Check size={14} strokeWidth={3} />
                      </button>
                      <button
                        onClick={() => setEditingLabels(prev => { const c = { ...prev }; delete c[item.key]; return c; })}
                        className="p-1 bg-zinc-300 hover:bg-zinc-400 rounded"
                        title="Cancelar"
                      >
                        <X size={14} strokeWidth={3} />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="font-bold">{item.label}</span>
                      <button
                        onClick={() => setEditingLabels(prev => ({ ...prev, [item.key]: item.label }))}
                        className="text-zinc-400 hover:text-indigo-600"
                        title="Renomear"
                        data-testid={`edit-label-${item.key}`}
                      >
                        <Pencil size={12} />
                      </button>
                    </>
                  )}
                  <span className="text-xs text-zinc-400">({item.key})</span>
                  {item.custom && (
                    <span className="text-[10px] bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full font-bold">
                      PERSONALIZADO
                    </span>
                  )}
                  {item.position_required && item.position_required.length > 0 && (
                    <span className="text-[10px] bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                      {item.position_required.join('+')}
                    </span>
                  )}
                </div>
              </div>

              <input
                type="number"
                step="0.1"
                value={displayWeight}
                onChange={(e) => setEditingWeights(prev => ({ ...prev, [item.key]: e.target.value }))}
                className={`w-20 text-center px-2 py-1 border-2 rounded font-bold ${
                  item.weight > 0 ? 'border-green-400 text-green-700' : item.weight < 0 ? 'border-red-400 text-red-700' : 'border-zinc-300'
                }`}
                data-testid={`weight-input-${item.key}`}
              />
              <span className="text-xs text-zinc-500">pts</span>

              {isEditing && (
                <button
                  onClick={() => handleSaveWeight(item.key)}
                  className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white font-bold text-sm rounded flex items-center gap-1"
                  data-testid={`save-weight-${item.key}`}
                >
                  <Save size={14} /> Guardar
                </button>
              )}

              {item.custom && (
                <button
                  onClick={() => handleDeleteCustom(item.key)}
                  className="text-red-600 hover:text-red-800"
                  title="Remover campo personalizado"
                  data-testid={`delete-custom-${item.key}`}
                >
                  <Trash2 size={18} />
                </button>
              )}

              {/* Visibility toggle */}
              <label
                className="flex items-center gap-1 cursor-pointer select-none"
                title={enabled ? 'Ativado (visível)' : 'Desativado (oculto em todo o lado)'}
              >
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => handleToggle(item.key, e.target.checked)}
                  className="w-4 h-4 accent-indigo-600 cursor-pointer"
                  data-testid={`toggle-visible-${item.key}`}
                />
                <span className={`text-xs font-bold uppercase ${enabled ? 'text-indigo-700' : 'text-zinc-400'}`}>
                  {enabled ? 'Visível' : 'Oculto'}
                </span>
              </label>
            </div>
          );
        })}
      </div>

      {/* Add custom field */}
      {!showAdd ? (
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded flex items-center gap-2"
          data-testid="show-add-field"
        >
          <Plus size={16} /> Adicionar Campo Personalizado
        </button>
      ) : (
        <div className="p-4 bg-white border-2 border-indigo-300 rounded">
          <h4 className="font-bold mb-3">Novo Campo Personalizado</h4>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-bold mb-1">Nome visível</label>
              <input
                type="text"
                value={newField.label}
                onChange={(e) => setNewField({ ...newField, label: e.target.value, key: !newField.key ? e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '_') : newField.key })}
                placeholder="Ex: Cartão Amarelo"
                className="w-full px-2 py-1 border-2 rounded"
                data-testid="new-field-label"
              />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Chave (sem espaços)</label>
              <input
                type="text"
                value={newField.key}
                onChange={(e) => setNewField({ ...newField, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, '_') })}
                placeholder="cartao_amarelo"
                className="w-full px-2 py-1 border-2 rounded font-mono text-sm"
                data-testid="new-field-key"
              />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Peso (pode ser negativo)</label>
              <input
                type="number"
                step="0.1"
                value={newField.weight}
                onChange={(e) => setNewField({ ...newField, weight: e.target.value })}
                className="w-full px-2 py-1 border-2 rounded"
                data-testid="new-field-weight"
              />
            </div>
            <div>
              <label className="block text-xs font-bold mb-1">Aplica a</label>
              <select
                value={newField.position}
                onChange={(e) => setNewField({ ...newField, position: e.target.value })}
                className="w-full px-2 py-1 border-2 rounded"
                data-testid="new-field-position"
              >
                {POSITION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddField} className="px-3 py-1 bg-green-600 text-white font-bold text-sm rounded" data-testid="confirm-add-field">✓ Adicionar</button>
            <button onClick={() => { setShowAdd(false); setNewField({ label: '', key: '', weight: 1, position: 'all' }); }} className="px-3 py-1 bg-zinc-300 font-bold text-sm rounded">Cancelar</button>
          </div>
        </div>
      )}

      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-300 rounded text-xs">
        <p><strong>ℹ️ Nota:</strong> O número de <strong>Jogos</strong> NÃO influencia os Pontos_Rating — serve apenas como contador de presença. Para que jogos contem, configura &quot;Vitória&quot; e &quot;Empate&quot; (que o admin lança manualmente após cada sessão).</p>
      </div>
        </div>
      )}
    </div>
  );
};

export default RatingConfigSection;
